import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ItemTag } from './models';
import { baseApi } from '@centerpiece/components';

export const item_tag = baseApi
  .enhanceEndpoints({ addTagTypes: ['ItemTags', 'ItemTag'] })
  .injectEndpoints({
    endpoints: builder => ({
      getItemTags: builder.query<ItemTag[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'item_tag',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'ItemTag' as const, id })),
                'ItemTags',
              ]
            : ['ItemTags'],
      }),
      getItemTag: builder.query<ItemTag, string>({
        query: id => ({
          url: `item_tag/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'ItemTag', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createItemTag: builder.mutation({
        query: request => ({
          url: 'item_tag',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['ItemTags'],
      }),
      updateItemTag: builder.mutation<ItemTag, ItemTag>({
        query: request => ({
          url: `item_tag/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'ItemTag', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            item_tag.util.updateQueryData('getItemTag', `${id}`, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(item_tag.util.invalidateTags(['ItemTags']))
             */
            dispatch(item_tag.util.invalidateTags(['ItemTags']));
          }
        },
      }),
      deleteItemTag: builder.mutation<ItemTag, number>({
        query: id => ({
          url: `item_tag/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'ItemTag' as const, arg }, 'ItemTags']
            : ['ItemTags'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetItemTagsQuery,
  useGetItemTagQuery,
  useCreateItemTagMutation,
  useUpdateItemTagMutation,
  useDeleteItemTagMutation,
  usePrefetch: useItemTagsPrefetch,
  useLazyGetItemTagsQuery,
} = item_tag;
const selectItemTagsRoot = item_tag.endpoints.getItemTags.select({});
export const selectItemTags = createSelector(
  selectItemTagsRoot,
  item_tag => item_tag.data || []
);

export const subscribeItemTags = item_tag.endpoints.getItemTags.useQueryState;
